import Vue from "vue";
import VueSimpleSuggest from "vue-simple-suggest";
import SettingPopup from "@/components/dialogs/quotation-item";
import PownganRollPicker from "@/components/dialogs/powngan-roll-picker";

const STATUS_OPTIONS = [
  { value: 1, text: "รอผู้ผลิตคอนเฟิร์ม" },
  { value: 2, text: "คอนเฟิร์มแล้ว (พร้อมออกใบผลิต)" },
  { value: 4, text: "ออกใบผลิตแล้ว" },
  { value: 6, text: "ยกเลิก" },
];

export default {
  components: { VueSimpleSuggest, SettingPopup, PownganRollPicker },

  data() {
    return {
      loading: true,
      submitting: false,

      role: "",
      userId: null,
      branch: "",

      order: null,
      pricingItems: [],

      // edit-mode state
      editItems: [],
      skuOptions: [],
      skuLoaded: false,

      // gear popup state
      isSettingOpened: false,
      settingData: {},
      settingIndex: -1,

      // popup เลือกม้วนก่อนออกใบผลิต
      isRollPickerOpened: false,

      // เอกสารแยกเวอร์ชัน (ver 1 = ตอนสร้าง, +1 ทุกครั้งที่แก้รายการ)
      versions: [],
      versionsLoading: false,
      showVersions: false,
      selectedVersion: null,

      // ประวัติการแก้ไข (powngan_order_histories)
      histories: [],
      historyLoading: false,
      showHistory: false,
      acknowledging: false,
    };
  },

  computed: {
    // ชนิดสินค้าของรายการที่เปิด gear popup อยู่ — ใช้คุมว่าติดฉนวนได้ไหม
    // รายการที่โหลดมาจาก API มี sku.main_product_id ติดมาด้วย
    // ส่วนแถวที่เพิ่งเลือก SKU ใหม่จะเซ็ต main_product_id ไว้ที่ row ตรง ๆ
    settingItemMainProductId() {
      const it = this.settingData;
      if (!it) return null;
      if (it.main_product_id !== undefined && it.main_product_id !== null) {
        return it.main_product_id;
      }
      return (it.sku && it.sku.main_product_id) || null;
    },

    isOwnerSide() {
      return this.order && Number(this.branch) === Number(this.order.owner_branch_id);
    },
    isProducerSide() {
      return this.order && Number(this.branch) === Number(this.order.producer_branch_id);
    },
    isAdmin() {
      return this.role === "Admin";
    },
    // เสมียนธรรมดา (สาขาที่เปิดให้สั่งโป๊ว) — ทำได้แค่สร้าง/ดู/แนบรูป ปุ่มระดับหัวหน้าต้องไม่โผล่
    // (backend ตอบ 401 อยู่แล้ว แต่ไม่ควรให้กดแล้วเจอ error)
    isClerk() {
      return this.role === "Customer assistant";
    },

    // step: producer ยืนยันรับงาน (status 1 -> 2)
    canProducerConfirm() {
      if (!this.order) return false;
      if (Number(this.order.status) !== 1) return false;
      // เสมียนฝั่งผู้ผลิตกดไม่ได้ (backend ensureAllowedRole ไม่รับ Customer assistant)
      if (this.isClerk) return false;
      return this.isAdmin || this.isProducerSide;
    },

    // step: producer ออกใบผลิต (status 2 -> 4)
    canIssueProduction() {
      if (!this.order) return false;
      if (Number(this.order.status) !== 2) return false;
      // ทุก role ของสาขาผู้รับผลิตออกได้ รวมเสมียน (ตกลงกับเจ้าของงาน 2026-10-13) — ตรงกับ backend issueProduction()
      return this.isAdmin || this.isProducerSide;
    },

    // เห็นราคา: เฉพาะระดับ ผจก. (Admin / ผจก.สาขา)
    // ผู้ช่วย ผจก.สาขา + ผจก.เสมียน เข้าใบสั่งโป๊วได้ แต่ไม่เห็นตัวเลขราคา (backend ก็ไม่ส่งมาแล้ว)
    canSeePricing() {
      return this.isAdmin || this.role === "Branch Admin";
    },

    // กรอกราคา: เฉพาะ Branch Admin ของสาขาผู้รับผลิต (หรือ Admin) — ทุก status ยกเว้น cancelled
    canEditPricing() {
      if (!this.order) return false;
      if (Number(this.order.status) === 6) return false;
      if (this.isAdmin) return true;
      return this.role === "Branch Admin" && this.isProducerSide;
    },

    // ระดับผู้จัดการ = Admin / ผจก.สาขา — แก้ไขรายการและยกเลิกใบผลิตได้เฉพาะระดับนี้
    isManager() {
      return this.role === "Admin" || this.role === "Branch Admin";
    },

    // แนบ/ลบไฟล์: ผจก. ตามกติกาแก้ไขรายการ + ข้อยกเว้นฝั่งสั่งตอนรอคอนเฟิร์ม (คนสร้างใบแนบรูปเพิ่มได้)
    canManageAttachments() {
      if (this.canEditOrder) return true;
      return !!this.order && Number(this.order.status) === 1 && this.isOwnerSide;
    },

    // ยกเลิกใบผลิต: ฝั่งรับผลิต + Admin เฉพาะตอนออกใบผลิตแล้ว (status 4) — ตรงกับ cancelProduction()
    canCancelProduction() {
      if (!this.order || !this.order.production_document) return false;
      if (Number(this.order.status) !== 4) return false;
      if (!this.isManager) return false;
      return this.isAdmin || this.isProducerSide;
    },

    // แก้ไขใบสั่ง (รายการ) — ต้องตรงกับ PownganController::ensureCanEditItems()
    //   ทั้งฝั่งสั่งและฝั่งรับผลิต + Admin: แก้ได้ตลอด (status 1, 2, 4)
    //   status 4: ถ้าแก้สเปกที่กระทบใบผลิต backend ตอบ 409 → ถามยืนยันยกเลิกใบผลิต+คืนม้วน | status 6 แก้ไม่ได้
    //   ทุกการแก้ลงประวัติ; ผู้รับผลิตแก้ → ฝั่งสั่งเห็น badge จนกดรับทราบ (ไม่ต้องยืนยันใหม่)
    //   แก้รายการ = สิทธิ์ระดับผู้จัดการ (Admin / ผจก.สาขา) เท่านั้น — backend บังคับด้วย canSeePricing()
    // ผู้สร้างใบแก้ใบตัวเองได้จนกว่าจะออกใบผลิต (status 1, 2) ทุก role — ตรงกับ backend ensureCanEditItems()
    isCreatorEditable() {
      if (!this.order || !this.userId) return false;
      const status = Number(this.order.status);
      return Number(this.order.created_by) === Number(this.userId) && (status === 1 || status === 2);
    },
    canEditOrder() {
      if (!this.order) return false;
      const status = Number(this.order.status);
      if (status === 6) return false;
      if (this.isCreatorEditable) return true;
      if (!this.canSeePricing) return false;
      return this.isAdmin || this.isProducerSide || this.isOwnerSide;
    },

    // แถบแจ้ง "ผู้รับผลิตแก้ไข" + ปุ่มรับทราบ — เห็นเฉพาะฝั่งสั่ง (และ Admin)
    showProducerEditNotice() {
      return !!(this.order && this.order.has_unacked_producer_edit && (this.isOwnerSide || this.isAdmin));
    },
    // ปุ่ม "รับทราบ" เป็นของหัวหน้าฝั่งสั่ง — เสมียนเห็นแจ้งเตือนได้แต่กดรับทราบไม่ได้
    canAcknowledge() {
      return this.showProducerEditNotice && !this.isClerk;
    },
    // ฝั่งรับผลิตเองก็ควรรู้ว่าฝั่งสั่งยังไม่เห็นการแก้
    showProducerEditPendingHint() {
      return !!(this.order && this.order.has_unacked_producer_edit && this.isProducerSide && !this.isAdmin);
    },

    // กลับด้าน: ฝั่งสั่งแก้ → ฝั่งรับผลิต (และ Admin) เห็น badge + ปุ่มรับทราบ
    showOwnerEditNotice() {
      return !!(this.order && this.order.has_unacked_owner_edit && (this.isProducerSide || this.isAdmin));
    },
    canAcknowledgeOwnerEdit() {
      return this.showOwnerEditNotice && !this.isClerk;
    },
    // ฝั่งสั่งเองก็ควรรู้ว่าฝั่งรับผลิตยังไม่เห็นการแก้
    showOwnerEditPendingHint() {
      return !!(this.order && this.order.has_unacked_owner_edit && this.isOwnerSide && !this.isAdmin);
    },

    sumSteel() {
      return (this.pricingItems || []).reduce((a, b) => a + Number(b.steel_total || 0), 0);
    },
    sumCut() {
      return (this.pricingItems || []).reduce((a, b) => a + Number(b.cut_total || 0), 0);
    },
    // ค่าแรงเลิกใช้แล้ว (รวมเป็นค่าตัด) — ยอดรวม = ค่าเหล็ก + ค่าตัด
    sumGrand() {
      return this.sumSteel + this.sumCut;
    },

    canCancel() {
      if (!this.order) return false;
      const s = Number(this.order.status);
      if (s === 4 || s === 6) return false;
      if (this.isClerk) return false;
      return this.isAdmin || this.isOwnerSide || this.isProducerSide;
    },
    // ยกเลิกพลาด → กู้คืนได้ (เฉพาะใบที่ยกเลิกอยู่ สิทธิ์เดียวกับการยกเลิก)
    canUndoCancel() {
      if (!this.order) return false;
      if (Number(this.order.status) !== 6) return false;
      if (this.isClerk) return false;
      return this.isAdmin || this.isOwnerSide || this.isProducerSide;
    },
  },

  methods: {
    // หน่วยของรายการ (เมตร/กิโล/ตร.ม. ฯลฯ) — หัวตารางไม่ฟิกซ์ (ม.) แล้ว
    // เพราะโป๊วงานรับได้หลายหน่วย ต้องโชว์หน่วยจริงต่อแถวให้ตรงกับที่กรอก
    unitLabel(it) {
      const u = (it && (it.unit_name || it.unit)) || ''
      return String(u).trim() || 'ม.'
    },
    toThaiDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" });
    },

    // พิมพ์ใบโป๊ว (แบบฟอร์มใบโป๊ว หน้าตาตาม Excel สาขา)
    async printOrder() {
      if (!this.order?.uuid) return;
      try {
        const result = await Vue.prototype.$axios.get(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/pdf`,
          { responseType: "blob" }
        );
        const blob = new Blob([result.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(blob), "targetWindow", "width=1100,height=800");
      } catch (error) {
        this.$swal({ icon: "error", title: "พิมพ์ไม่สำเร็จ", text: error?.response?.data?.message || "" });
      }
    },
    // พิมพ์ใบผลิตโป๊วงาน (PDW) ที่ออกจากใบสั่งนี้
    async printProduction() {
      if (!this.order?.production_document?.uuid) return;
      try {
        const result = await Vue.prototype.$axios.get(
          `${Vue.prototype.$host}/api/v1/document/powngan-productions/${this.order.production_document.uuid}/pdf`,
          { responseType: "blob" }
        );
        const blob = new Blob([result.data], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "targetWindow", "width=900,height=700");
      } catch (error) {
        this.$swal({ icon: "error", title: "พิมพ์ไม่สำเร็จ", text: error?.response?.data?.message || "" });
      }
    },

    toMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return "0.00";
      return n.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    statusText(s) {
      const f = STATUS_OPTIONS.find((x) => x.value === Number(s));
      return f ? f.text : s;
    },

    statusVariant(s) {
      const n = Number(s);
      if (n === 1) return "secondary";
      if (n === 2) return "info";
      if (n === 4) return "success";
      if (n === 6) return "danger";
      return "light";
    },

    toThaiDateTime(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleString("th-TH", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    },

    // ---------- ประวัติการแก้ไข ----------
    historyActionText(a) {
      const map = {
        created: "สร้างใบสั่ง",
        items_updated: "แก้ไขรายการ",
        pricing_updated: "กรอก/แก้ราคา",
        producer_confirmed: "ผู้รับผลิตยืนยันรับงาน",
        production_issued: "ออกใบผลิต",
        production_cancelled: "ยกเลิกใบผลิต",
        cancelled: "ยกเลิกใบสั่ง",
        undo_cancelled: "กู้คืนใบสั่ง",
        attachment_added: "แนบรูป",
        attachment_deleted: "ลบรูป",
        owner_acknowledged: "ฝั่งสั่งรับทราบ",
        producer_acknowledged: "ฝั่งรับผลิตรับทราบ",
      };
      return map[a] || a;
    },
    historyActionVariant(a) {
      if (a === "items_updated" || a === "attachment_added" || a === "attachment_deleted") return "warning";
      if (a === "pricing_updated") return "primary";
      if (a === "cancelled" || a === "production_cancelled") return "danger";
      if (a === "production_issued" || a === "producer_confirmed") return "success";
      if (a === "owner_acknowledged" || a === "producer_acknowledged") return "info";
      return "secondary";
    },
    historySideText(side) {
      if (side === "owner") return "ฝั่งสั่ง";
      if (side === "producer") return "ฝั่งรับผลิต";
      if (side === "admin") return "Admin";
      return "";
    },

    async loadHistory() {
      if (!this.order?.uuid) return;
      this.historyLoading = true;
      try {
        const res = await Vue.prototype.$axios.get(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/history`
        );
        this.histories = res?.data?.data || [];
      } catch (e) {
        this.histories = [];
      } finally {
        this.historyLoading = false;
      }
    },

    // ---------- เวอร์ชันเอกสาร ----------
    async loadVersions() {
      if (!this.order?.uuid) return;
      this.versionsLoading = true;
      try {
        const res = await Vue.prototype.$axios.get(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/versions`
        );
        this.versions = res?.data?.data || [];
        // เปิดมาให้เห็นเวอร์ชันล่าสุดก่อน
        const last = this.versions[this.versions.length - 1];
        if (!this.selectedVersion || !this.versions.find((v) => v.version === this.selectedVersion)) {
          this.selectedVersion = last ? last.version : null;
        }
      } catch (e) {
        this.versions = [];
      } finally {
        this.versionsLoading = false;
      }
    },
    toggleVersions() {
      this.showVersions = !this.showVersions;
      if (this.showVersions && !this.versions.length) this.loadVersions();
    },
    selectedVersionData() {
      return (this.versions || []).find((v) => v.version === this.selectedVersion) || null;
    },
    versionSubtitle(v) {
      if (!v) return "";
      const who = v.changed_by_name || "-";
      const side = this.historySideText(v.side);
      return `${this.toThaiDateTime(v.created_at)} · ${who}${side ? " (" + side + ")" : ""}`;
    },
    // บรรทัดสรุปการเปลี่ยนแปลงของเวอร์ชัน (ตัดบรรทัด "ver X → ver Y" ที่ซ้ำกับหัวออก)
    versionChangeLines(v) {
      return ((v && v.summary) || []).filter((l) => !/^ver \d+ → ver \d+$/.test(l));
    },

    toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory && !this.histories.length) this.loadHistory();
    },

    // ฝั่งสั่งกดรับทราบการแก้ของผู้รับผลิต — แค่ล้าง badge สถานะใบไม่เปลี่ยน
    async onAcknowledge() {
      if (!this.order?.uuid) return;
      this.acknowledging = true;
      try {
        await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/acknowledge`
        );
        await this.loadOrder();
        if (this.showHistory) await this.loadHistory();
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "รับทราบไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.acknowledging = false;
      }
    },

    // ฝั่งรับผลิตกดรับทราบการแก้ของฝั่งสั่ง — แค่ล้าง badge สถานะใบไม่เปลี่ยน
    async onAcknowledgeOwner() {
      if (!this.order?.uuid) return;
      this.acknowledging = true;
      try {
        await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/acknowledge-owner`
        );
        await this.loadOrder();
        if (this.showHistory) await this.loadHistory();
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "รับทราบไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.acknowledging = false;
      }
    },

    // ราคา 2 ตัวตาม Excel: ราคาทุน/ม. + ค่าตัด/ม. (ค่าแรงเลิกใช้ = อันเดียวกับค่าตัด)
    // ทั้งคู่คิดต่อเมตร × ปริมาณรวม × ตัวคูณลอน — มิเรอร์สูตร backend
    recalcPricing(it) {
      const steel = Number(it.steel_price_per_unit || 0);
      const cut = Number(it.cut_price_per_unit || 0);
      const mult = Number(it.roll_multiplier) || 1;
      const billable = Number(it.total_length || 0) * mult;
      it.steel_total = +(steel * billable).toFixed(2);
      it.cut_total = +(cut * billable).toFixed(2);
      it.line_total = +(it.steel_total + it.cut_total).toFixed(2);
    },

    async onUploadAttachment(itemId, event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          this.$swal({ icon: "error", title: "ไฟล์ไม่ใช่รูปภาพ", text: file.name });
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          this.$swal({ icon: "error", title: "ไฟล์ใหญ่เกิน 10MB", text: file.name });
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
          await Vue.prototype.$axios.post(
            `${Vue.prototype.$host}/api/v1/document/powngan/items/${itemId}/attachments`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        } catch (error) {
          this.$swal({
            icon: "error",
            title: "อัพโหลดไม่สำเร็จ",
            text: error?.response?.data?.message || error?.message || file.name,
          });
        }
      }
      event.target.value = "";
      await this.loadOrder();
    },

    async onDeleteAttachment(attachmentId) {
      const confirm = await this.$swal({
        title: "ลบไฟล์แนบ?",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
      });
      if (!confirm.value) return;

      try {
        await Vue.prototype.$axios.delete(
          `${Vue.prototype.$host}/api/v1/document/powngan/attachments/${attachmentId}`
        );
        await this.loadOrder();
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "ลบไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      }
    },

    async loadSkuOptions() {
      try {
        const result = await Vue.prototype.$axios.get(`${Vue.prototype.$host}/api/v1/master/sku`);
        this.skuOptions = result?.data || [];
        this.skuLoaded = true;
      } catch (e) {
        this.skuOptions = [];
      }
    },

    skuFilter(item, query) {
      if (!query) return true;
      const q = String(query).toLowerCase();
      return (
        String(item.full_code || "").toLowerCase().includes(q) ||
        String(item.name || "").toLowerCase().includes(q)
      );
    },

    onPickSku(suggestion, idx) {
      if (!suggestion) return;
      const row = this.editItems[idx];
      if (!row) return;
      row.sku_id = suggestion.id;
      row.code_description = suggestion.name;
      this.$set(row, "main_product_id", suggestion.main_product_id ?? null);
      // ตัวคูณลอนจาก SKU (ลอนปกติ = 1)
      this.$set(row, "roll_multiplier", Number(suggestion.roll_multiplier) || 1);
      // เปลี่ยนเป็นสินค้าที่ติดฉนวนไม่ได้ ต้องล้างค่าฉนวนเดิมทิ้ง
      this.clearInsulatorIfNotAllowed(row);
      setTimeout(() => {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      }, 10);
    },

    onTypeSku(e, idx) {
      if (!this.skuLoaded) return;
      const q = typeof e === "string" ? e : e?.full_code || e?.target?.value || "";
      const row = this.editItems[idx];
      if (!row) return;
      const lc = String(q).toLowerCase();
      const i = this.skuOptions.findIndex(
        (p) =>
          String(p.full_code || "").toLowerCase() === lc ||
          String(p.name || "").toLowerCase() === lc
      );
      if (i !== -1) {
        row.sku_id = this.skuOptions[i].id;
        row.code_description = this.skuOptions[i].name;
        this.$set(row, "main_product_id", this.skuOptions[i].main_product_id ?? null);
        this.$set(row, "roll_multiplier", Number(this.skuOptions[i].roll_multiplier) || 1);
      } else {
        row.sku_id = null;
        row.code_description = "";
        this.$set(row, "main_product_id", null);
        this.$set(row, "roll_multiplier", 1);
      }
      this.clearInsulatorIfNotAllowed(row);
    },

    // ติดฉนวนได้เฉพาะแผ่น (1) / ครอบ (2) — เปลี่ยน SKU เป็นอย่างอื่นต้องล้างค่าฉนวน
    // ไม่งั้นสกรูจะพก is_insulator ติดไปด้วย (เจอจริงในข้อมูลเก่า)
    clearInsulatorIfNotAllowed(row) {
      if (!row) return;
      const t = Number(
        row.main_product_id !== undefined && row.main_product_id !== null
          ? row.main_product_id
          : (row.sku && row.sku.main_product_id)
      );
      if ([1, 2].indexOf(t) !== -1) return;
      this.$set(row, "is_insulator", false);
      this.$set(row, "isPE", true);
      this.$set(row, "isPU", false);
      this.$set(row, "thickness", null);
      this.$set(row, "insulator_material", null);
      this.$set(row, "insulator_detail", "");
      this.$set(row, "insulator_head", "");
      this.$set(row, "insulator_tail", "");
    },

    addEditRow() {
      this.editItems.push({
        sku_id: null,
        code: "",
        code_description: "",
        description: "",
        unit_name: "",
        length: 0,
        quantity: 0,
        roll_multiplier: 1,
        total_length: 0,
        remark: "",
        attachments: [],
        is_insulator: false,
        isPE: true,
        isPU: false,
        thickness: null,
        insulator_material: null,
        insulator_detail: "",
        insulator_head: "",
        insulator_tail: "",
        is_curve: false,
        curve_weight: "",
        curve_start: "",
        curve_gap: "",
        curve_repeat: "",
      });
    },

    // คัดลอกรายการ — ได้ทุกอย่างรวม option/ตัวคูณลอน
    // ⚠️ ต้องตัด id + attachments ทิ้ง ไม่งั้น backend (smart upsert ตาม id) จะไปทับแถวต้นฉบับ
    // แถวใหม่ไม่มี id = insert ใหม่; รูปแนบต้องอัพใหม่หลังบันทึก (แถวใหม่ยังไม่มี item id)
    copyEditRow(idx) {
      const src = this.editItems[idx];
      if (!src) return;
      const { id, attachments, ...rest } = src;
      this.editItems.splice(idx + 1, 0, { ...rest, attachments: [] });
    },

    removeEditRow(idx) {
      this.editItems.splice(idx, 1);
    },

    // สรุป option ที่ตั้งจาก popup (เฟือง) มาแสดงใต้ชื่อ SKU
    // thickness/insulator_material เก็บเป็น uuid → map เป็นชื่อผ่าน global helper mixin
    optionSummary(row) {
      if (!row) return "";
      const parts = [];

      if (row.is_insulator) {
        let t = "ติดฉนวน";
        if (row.isPU) {
          t += " PU";
          const o = (this.thicknessOptions2 || []).find((x) => x.uuid == row.thickness);
          if (o) t += " " + o.name;
        } else if (row.isPE) {
          t += " PE";
          const o = (this.thicknessOptions || []).find((x) => x.uuid == row.thickness);
          if (o) t += " " + o.name;
        }
        const mat = (this.insulatorMaterialOptions || []).find((x) => x.uuid == row.insulator_material);
        if (mat) t += " ท้อง" + mat.name;
        if (row.insulator_detail) t += " " + row.insulator_detail;
        if (row.insulator_head) t += " / เว้นหัว " + row.insulator_head;
        if (row.insulator_tail) t += " เว้นท้าย " + row.insulator_tail;
        parts.push(t);
      }

      if (row.is_curve) {
        let c = "ย้ำโค้ง";
        if (row.curve_weight) c += " " + row.curve_weight;
        if (row.curve_start) c += " เริ่ม " + row.curve_start;
        if (row.curve_gap) c += " ห่าง " + row.curve_gap;
        if (row.curve_repeat) c += " ย้ำ " + row.curve_repeat;
        parts.push(c);
      }

      return parts.join(" · ");
    },

    // แยกบรรทัด: ติดฉนวน / ย้ำโค้ง อ่านง่ายกว่าต่อกันด้วย · บรรทัดเดียว
    optionSummaryLines(row) {
      const t = this.optionSummary(row);
      return t ? String(t).split(" · ").filter((x) => x && x.trim()) : [];
    },

    // ความยาวรวมของแถว = ความยาว/เส้น × จำนวน (ปริมาณผลิตจริง)
    // ⚠️ ไม่คูณตัวคูณลอน — ตัวคูณลอนแปลกใช้ตอนคิดเงินเท่านั้น (backend คูณตอน pricing)
    rowTotalLength(row) {
      const len = Number(row?.length) || 0;
      const qty = Number(row?.quantity) || 0;
      return Math.round(len * qty * 100) / 100;
    },

    openEditSetting(idx) {
      this.settingIndex = idx;
      this.settingData = this.editItems[idx];
      this.isSettingOpened = true;
    },

    onEditSettingClose() {
      this.isSettingOpened = false;
      // popup เขียนค่ากลับเข้ามาที่ object เดิม (reference) อยู่แล้ว
    },

    async onSaveOrderEdits() {
      // validate SKU + qty
      const skuIdSet = new Set(this.skuOptions.map((s) => s.id));
      const noSku = [];
      const noQty = [];
      const noLength = [];
      this.editItems.forEach((it, idx) => {
        if (!it.sku_id || !skuIdSet.has(it.sku_id)) noSku.push(idx + 1);
        if (!it.quantity || Number(it.quantity) <= 0) noQty.push(idx + 1);
        // ความยาวเป็นฟิลด์บังคับ (มี * แดงที่หัวคอลัมน์) — ใช้คิดปริมาณรวม/ราคา
        if (!it.length || Number(it.length) <= 0) noLength.push(idx + 1);
      });
      if (!this.editItems.length) {
        return this.$swal({ icon: "error", title: "ต้องมีรายการอย่างน้อย 1 รายการ" });
      }
      if (noSku.length) {
        return this.$swal({ icon: "error", title: `รายการลำดับที่ ${noSku.join(", ")} ยังไม่ได้เลือก SKU` });
      }
      if (noQty.length) {
        return this.$swal({ icon: "error", title: `รายการลำดับที่ ${noQty.join(", ")} ต้องระบุจำนวน > 0` });
      }
      if (noLength.length) {
        return this.$swal({ icon: "error", title: `รายการลำดับที่ ${noLength.join(", ")} ต้องระบุความยาว > 0` });
      }

      this.submitting = true;
      try {
        // ส่ง products พร้อม id (ถ้ามี) เพื่อ smart upsert + option fields ครบ
        const products = this.editItems.map((it) => ({
          id: it.id || null,
          sku_id: it.sku_id,
          code: it.code,
          code_description: it.code_description,
          description: it.description,
          unit_name: it.unit_name,
          length: Number(it.length || 0),
          quantity: Number(it.quantity || 0),
          total_length: this.rowTotalLength(it),
          roll_multiplier: Number(it.roll_multiplier) || 1,
          remark: it.remark,
          is_insulator: !!it.is_insulator,
          isPE: !!it.isPE,
          isPU: !!it.isPU,
          thickness: it.thickness,
          insulator_material: it.insulator_material,
          insulator_detail: it.insulator_detail,
          insulator_head: it.insulator_head,
          insulator_tail: it.insulator_tail,
          is_curve: !!it.is_curve,
          curve_weight: it.curve_weight,
          curve_start: it.curve_start,
          curve_gap: it.curve_gap,
          curve_repeat: it.curve_repeat,
        }));

        const url = `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}`;
        const body = { products: JSON.stringify(products) };
        let saved;
        try {
          saved = await Vue.prototype.$axios.patch(url, body);
          // บาง service ในโปรเจกต์ตั้ง validateStatus ให้ไม่ throw — เช็คเองกันพลาด
          if (saved && saved.status >= 400) throw { response: saved };
        } catch (err) {
          // ออกใบผลิตแล้ว + แก้สเปกที่ใบผลิตใช้ → ต้องยืนยันยกเลิกใบผลิต/คืนม้วนก่อน แล้วส่งซ้ำ
          const d = err?.response?.data;
          if (err?.response?.status !== 409 || !d?.requires_reissue) throw err;
          if (!(await this.confirmReissue(d))) return;
          saved = await Vue.prototype.$axios.patch(url, { ...body, confirm_reissue: true });
          if (saved && saved.status >= 400) throw { response: saved };
        }
        await this.loadOrder();
        if (this.showHistory) await this.loadHistory();
        if (this.showVersions) { this.selectedVersion = null; await this.loadVersions(); }
        // backend ส่งรายการที่เปลี่ยนจริงกลับมา — โชว์ให้คนแก้เห็นว่าอะไรถูกบันทึกลงประวัติ
        // (กรณียกเลิกใบผลิตอัตโนมัติ บรรทัด "ยกเลิกใบผลิต ... + คืนม้วน" อยู่ใน changes ด้วย)
        const changes = saved?.data?.changes || [];
        const changed = saved?.data?.changed !== false;
        const esc = (x) => String(x ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
        this.$swal({
          icon: changed ? "success" : "info",
          title: changed ? "บันทึกสำเร็จ" : "ไม่มีการเปลี่ยนแปลง",
          html: changes.length
            ? `<div class="text-left small">${changes.map((c) => `• ${esc(c)}`).join("<br>")}</div>`
            : "",
          confirmButtonText: "ตกลง",
        });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "บันทึกไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.submitting = false;
      }
    },

    // แจ้งผลกระทบก่อนยกเลิกใบผลิตอัตโนมัติ: เปลี่ยนอะไร / ใบผลิตไหนถูกยกเลิก / ม้วนไหนได้เมตรคืน
    async confirmReissue(d) {
      const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const li = (arr) => arr.map((x) => `<li>${x}</li>`).join("");
      const reasons = li((d.reasons || []).map(esc));
      const rolls = li((d.roll_restore || []).map((r) => `${esc(r.coil)} คืน ${this.toMoney(r.length)} ม.`));
      const docs = (d.production_documents || []).map(esc).join(", ");
      const confirm = await this.$swal({
        icon: "warning",
        title: "การแก้ไขนี้กระทบใบผลิต",
        html:
          `<div class="text-left" style="font-size:14px;">` +
          `<div><b>สิ่งที่แก้:</b></div><ul>${reasons}</ul>` +
          `<div><b>ใบผลิตที่จะถูกยกเลิก:</b> ${docs || "-"}</div>` +
          (rolls ? `<div class="mt-2"><b>ม้วนที่จะได้ความยาวคืน:</b></div><ul>${rolls}</ul>` : "") +
          `<div class="mt-2 text-danger">หลังบันทึก ใบสั่งจะกลับเป็น "คอนเฟิร์มแล้ว" ต้องเลือกม้วนและออกใบผลิตใหม่</div>` +
          `</div>`,
        showCancelButton: true,
        confirmButtonText: "ยืนยัน ยกเลิกใบผลิตและบันทึก",
        cancelButtonText: "กลับไปแก้",
      });
      return !!confirm.value;
    },

    async onSavePricing() {
      const items = this.pricingItems.map((it) => ({
        id: it.id,
        steel_price_per_unit: Number(it.steel_price_per_unit || 0),
        cut_price_per_unit: Number(it.cut_price_per_unit || 0),
      }));

      this.submitting = true;
      try {
        await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/pricing`,
          { items: JSON.stringify(items) }
        );
        await this.loadOrder();
        if (this.showHistory) await this.loadHistory();
        this.$swal({ icon: "success", title: "บันทึกราคาสำเร็จ", confirmButtonText: "ตกลง" });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "บันทึกไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.submitting = false;
      }
    },

    async getRole() {
      try {
        const res = await this.$store.dispatch("Auth/getRole", {}, { root: true });
        this.role = res?.data?.role || "";
        this.branch = res?.data?.branch_id || "";
        this.userId = res?.data?.id || null;
      } catch (e) {
        this.role = "";
        this.branch = "";
        this.userId = null;
      }
    },

    async loadOrder() {
      const id = this.$route.params.id;
      if (!id) return;
      this.loading = true;
      try {
        const res = await Vue.prototype.$axios.get(`${Vue.prototype.$host}/api/v1/document/powngan/${id}`);
        const order = res?.data?.data || res?.data || null;
        this.order = order;
        this.pricingItems = (order?.items || []).map((it) => ({ ...it }));
        // ตัวคูณลอน: ใช้ค่าที่ snapshot ไว้ที่รายการเป็นหลัก (เงินในใบต้องไม่เปลี่ยนย้อนหลัง
        // เวลาแก้ตัวคูณที่ master) — ใบเก่าที่ยังไม่มีค่า fallback ไปอ่านจาก SKU ปัจจุบัน
        this.editItems = (order?.items || []).map((it) => {
          let mult = Number(it.roll_multiplier);
          if (!mult) {
            const sku = (this.skuOptions || []).find((s) => s.id === it.sku_id);
            mult = sku ? (Number(sku.roll_multiplier) || 1) : 1;
          }
          return { ...it, roll_multiplier: mult };
        });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "ไม่สามารถโหลดเอกสารได้",
          text: error?.response?.data?.message || error?.message || "",
        }).then(() => this.$router.push("/powngan"));
      } finally {
        this.loading = false;
      }
    },

    async onProducerConfirm() {
      const confirm = await this.$swal({
        title: "ยืนยันรับงานโป๊ว?",
        text: "หลังยืนยันจะพร้อมออกใบผลิต",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
      });
      if (!confirm.value) return;

      this.submitting = true;
      try {
        await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/producer-confirm`
        );
        await this.loadOrder();
        this.$swal({ icon: "success", title: "ยืนยันสำเร็จ", confirmButtonText: "ตกลง" });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "ยืนยันไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.submitting = false;
      }
    },

    // ออกใบผลิตต้องเลือกม้วนก่อนเสมอ (backend บังคับ) — เปิด popup เลือกม้วน
    onIssueProduction() {
      this.isRollPickerOpened = true;
    },

    onRollPickerClose() {
      this.isRollPickerOpened = false;
    },

    // popup ส่ง groups (จัดกลุ่ม + แบ่งความยาวต่อม้วนมาแล้ว) กลับมา
    async onRollPickerSubmit(payload) {
      const groups = (payload && payload.groups) || [];
      if (!groups.length) return;

      const confirm = await this.$swal({
        title: "ออกใบผลิต?",
        text: `จะออกใบผลิตทั้งหมด ${groups.length} ใบ`,
        showCancelButton: true,
        confirmButtonText: "ออกใบผลิต",
        cancelButtonText: "ยกเลิก",
      });
      if (!confirm.value) return;

      this.submitting = true;
      try {
        const result = await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/issue-production`,
          { groups }
        );

        // ปิด popup ก่อนทำอย่างอื่น — ถ้า loadOrder หรือ swal พลาด
        // popup จะได้ไม่ค้างเปิดทั้งที่ออกใบผลิตสำเร็จไปแล้ว
        this.isRollPickerOpened = false;

        const docNos = (result && result.data && result.data.production_document_nos) || [];
        this.$swal({
          icon: "success",
          title: "ออกใบผลิตสำเร็จ",
          text: docNos.join(", "),
          confirmButtonText: "ตกลง",
        });

        await this.loadOrder();
      } catch (error) {
        // ข้อความจาก backend บอกชัดว่าม้วนไหนไม่พอ / รายการไหนยังขาด
        // (ปล่อย popup เปิดไว้ ผู้ใช้จะได้แก้ม้วนแล้วกดใหม่ได้เลย)
        this.$swal({
          icon: "error",
          title: "ออกใบผลิตไม่สำเร็จ",
          text:
            (error && error.response && error.response.data && error.response.data.message) ||
            (error && error.message) ||
            "",
        });
      } finally {
        this.submitting = false;
      }
    },

    async onUndoCancel() {
      const confirm = await this.$swal({
        icon: "question",
        title: "กู้คืนใบสั่งโป๊ว?",
        text: "เอกสารจะกลับไปสถานะก่อนยกเลิก และเหตุผลการยกเลิกจะถูกล้าง",
        showCancelButton: true,
        confirmButtonText: "กู้คืน",
        cancelButtonText: "ปิด",
      });
      if (!confirm.value) return;

      this.submitting = true;
      try {
        await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/undo-cancel`
        );
        await this.loadOrder();
        this.$swal({ icon: "success", title: "กู้คืนสำเร็จ", confirmButtonText: "ตกลง" });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "กู้คืนไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.submitting = false;
      }
    },

    async onCancelProduction() {
      const docNo = this.order.production_document.document_no;
      const { value: reason } = await this.$swal({
        title: `ยกเลิกใบผลิต ${docNo}`,
        html: "ความยาวม้วนที่ตัดไปจะถูก<b>คืนกลับ</b> และใบสั่งจะกลับเป็นสถานะ<b>คอนเฟิร์มแล้ว</b><br>จากนั้นแก้ไขรายการแล้วออกใบผลิตใหม่ได้",
        input: "textarea",
        inputPlaceholder: "ระบุเหตุผล...",
        showCancelButton: true,
        confirmButtonText: "ยืนยันยกเลิกใบผลิต",
        cancelButtonText: "ปิด",
        inputValidator: (v) => (!v || !v.trim() ? "กรุณาระบุเหตุผล" : null),
      });
      if (!reason) return;

      this.submitting = true;
      try {
        const res = await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan-productions/${this.order.production_document.uuid}/cancel`,
          { cancel_reason: reason }
        );
        // บาง service ในโปรเจกต์ตั้ง validateStatus ให้ไม่ throw — เช็คเองกันพลาด
        if (res && res.status >= 400) throw { response: res };
        await this.loadOrder();
        this.$swal({ icon: "success", title: "ยกเลิกใบผลิตสำเร็จ", text: "แก้ไขรายการได้แล้ว", confirmButtonText: "ตกลง" });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "ยกเลิกใบผลิตไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.submitting = false;
      }
    },

    async onCancel() {
      const { value: reason } = await this.$swal({
        title: "ยกเลิกใบสั่งโป๊ว",
        input: "textarea",
        inputLabel: "เหตุผลในการยกเลิก",
        inputPlaceholder: "ระบุเหตุผล...",
        showCancelButton: true,
        confirmButtonText: "ยืนยันยกเลิก",
        cancelButtonText: "ปิด",
        inputValidator: (v) => (!v || !v.trim() ? "กรุณาระบุเหตุผล" : null),
      });
      if (!reason) return;

      this.submitting = true;
      try {
        await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan/${this.order.uuid}/cancel`,
          { cancel_reason: reason }
        );
        await this.loadOrder();
        this.$swal({ icon: "success", title: "ยกเลิกสำเร็จ", confirmButtonText: "ตกลง" });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "ยกเลิกไม่สำเร็จ",
          text: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        this.submitting = false;
      }
    },
  },

  async mounted() {
    // getRole ขนานได้; แต่ loadOrder ต้อง hydrate ตัวคูณลอนจาก skuOptions
    // → โหลด SKU ให้เสร็จก่อน แล้วค่อย loadOrder
    await Promise.all([this.getRole(), this.loadSkuOptions()]);
    await this.loadOrder();
  },
};
